import React, { useState, createElement } from 'react';
import { Download, ChevronDown, ChevronUp } from 'lucide-react';
import { FieldWithSources, fieldRequirementsData } from '../data/fieldRequirements';
import { getUniqueFeatures } from '../data/fieldRequirements';
interface FieldsTableProps {
  fields: FieldWithSources[];
  vmFields: FieldWithSources[];
  otherFields: FieldWithSources[];
  selectedProducts: string[];
  selectedFeatures: string[];
  availableFeatures: string[];
  showTabs: boolean;
}
export function FieldsTable({
  fields,
  vmFields,
  otherFields,
  selectedProducts,
  selectedFeatures,
  availableFeatures,
  showTabs
}: FieldsTableProps) {
  const [activeTab, setActiveTab] = useState<'other' | 'vm'>('other');
  const [showOptional, setShowOptional] = useState(false);
  const [showGiftData, setShowGiftData] = useState(true);
  const [showConstituentData, setShowConstituentData] = useState(true);
  // Check if we're in VM-only mode (only VM selected)
  const isVMOnlyMode = selectedProducts.length === 1 && selectedProducts[0] === 'VM';
  // Check if only feature-dependent products are selected without features
  const featureDependentProducts = ['Events', 'Outreach', 'Online Giving'];
  const productLevelProducts = ['Wealth', 'GO', 'VM'];
  const hasOnlyFeatureDependentProducts = selectedProducts.length > 0 && selectedProducts.every(p => featureDependentProducts.includes(p));
  const hasProductLevelProducts = selectedProducts.some(p => productLevelProducts.includes(p));
  const shouldShowFeatureMessage = hasOnlyFeatureDependentProducts && selectedFeatures.length === 0 && !hasProductLevelProducts;
  // Check if Smart Ask Amounts is selected
  const hasSmartAskAmounts = selectedProducts.includes('Online Giving') && (selectedFeatures.includes('Smart Ask Amounts') || selectedFeatures.length === 0);
  if (selectedProducts.length === 0) {
    return <div className="text-center py-8 sm:py-12 bg-white rounded-lg border border-gray-200" data-id="element-42">
        <p className="text-gray-500 text-sm sm:text-base px-4" data-id="element-43">
          Select one or more products to view field requirements
        </p>
      </div>;
  }
  if (shouldShowFeatureMessage) {
    const featuresByProduct: Record<string, string[]> = {};
    selectedProducts.forEach(product => {
      const productFeatures = getUniqueFeatures([product]);
      if (productFeatures.length > 0) {
        featuresByProduct[product] = productFeatures;
      }
    });
    return <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-8" data-id="element-44">
        <div className="max-w-2xl" data-id="element-45">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4" data-id="element-46">
            Feature Selection Required
          </h3>
          <p className="text-sm sm:text-base text-gray-700 mb-6" data-id="element-47">
            Your selection has specific features which require data to enable.
            Please select feature(s) to see what is required and recommended.
          </p>

          <div className="space-y-4" data-id="element-48">
            {Object.entries(featuresByProduct).map(([product, features]) => <div key={product} data-id="element-49">
                <h4 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-50">
                  {product}
                </h4>
                <ul className="list-disc list-inside space-y-1 ml-2" data-id="element-51">
                  {features.map(feature => <li key={feature} className="text-sm text-gray-700" data-id="element-52">
                      {feature}
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </div>;
  }
  if (fields.length === 0) {
    return <div className="text-center py-8 sm:py-12 bg-white rounded-lg border border-gray-200" data-id="element-53">
        <p className="text-gray-500 text-sm sm:text-base px-4" data-id="element-54">
          No fields match your current selection
        </p>
      </div>;
  }
  // Determine which fields to display based on tabs and mode
  const displayFields = showTabs ? activeTab === 'vm' ? vmFields : otherFields : isVMOnlyMode ? vmFields : fields;
  // Helper function to get required, recommended, and optional sources for a field
  const getSourcesByStatus = (field: FieldWithSources): {
    required: string[];
    recommended: string[];
    optional: string[];
  } => {
    const required: string[] = [];
    const recommended: string[] = [];
    const optional: string[] = [];
    // Filter sources based on active tab or VM-only mode
    const relevantSources = showTabs && activeTab === 'vm' || isVMOnlyMode ? field.sources.filter(source => source === 'VM' || source.startsWith('VM -')) : field.sources;
    // Look up each source in the original data to determine its status
    relevantSources.forEach(source => {
      // Find matching records in the original data
      const matchingRecords = fieldRequirementsData.filter(record => {
        // For GO, VM, and Wealth - source is just the product name
        const recordSource = record.product === 'GO' || record.product === 'VM' || record.product === 'Wealth' ? record.product : `${record.product} - ${record.feature === 'GC Wealth Alerts' ? 'Wealth Alerts' : record.feature === 'GC GO' ? 'GO' : record.feature}`;
        return record.field === field.field && recordSource === source;
      });
      // Check status of matching records
      const isRequired = matchingRecords.some(record => record.status === 'Required');
      const isRecommended = matchingRecords.some(record => record.status === 'Recommended');
      const isOptional = matchingRecords.some(record => record.status.startsWith('Optional'));
      if (isRequired) {
        required.push(source);
      } else if (isRecommended) {
        recommended.push(source);
      } else if (isOptional) {
        optional.push(source);
      }
    });
    return {
      required,
      recommended,
      optional
    };
  };
  // Helper function to check if a field is a Constituent field
  const isConstituentField = (field: FieldWithSources) => field.section.startsWith('Constituent');
  // Separate fields into Constituent, Gift Data, and Other
  const constituentFields = displayFields.filter(field => isConstituentField(field));
  const giftDataFields = displayFields.filter(field => field.section === 'Gift Data');
  const otherSectionFields = displayFields.filter(field => !isConstituentField(field) && field.section !== 'Gift Data');
  // Separate Constituent fields into required/recommended and optional
  const constituentRequiredRecommended = constituentFields.filter(field => {
    const {
      required,
      recommended
    } = getSourcesByStatus(field);
    return required.length > 0 || recommended.length > 0;
  });
  const constituentOptional = constituentFields.filter(field => {
    const {
      required,
      recommended,
      optional
    } = getSourcesByStatus(field);
    return required.length === 0 && recommended.length === 0 && optional.length > 0;
  });
  // Separate Other section fields into required/recommended and optional
  const otherRequiredRecommended = otherSectionFields.filter(field => {
    const {
      required,
      recommended
    } = getSourcesByStatus(field);
    return required.length > 0 || recommended.length > 0;
  });
  const otherOptionalFields = otherSectionFields.filter(field => {
    const {
      required,
      recommended,
      optional
    } = getSourcesByStatus(field);
    return required.length === 0 && recommended.length === 0 && optional.length > 0;
  });
  // Separate Gift Data fields into required/recommended and optional
  const giftDataRequiredRecommended = giftDataFields.filter(field => {
    const {
      required,
      recommended
    } = getSourcesByStatus(field);
    return required.length > 0 || recommended.length > 0;
  });
  const giftDataOptional = giftDataFields.filter(field => {
    const {
      required,
      recommended,
      optional
    } = getSourcesByStatus(field);
    return required.length === 0 && recommended.length === 0 && optional.length > 0;
  });
  // Count unique fields that have at least one required source
  const requiredCount = displayFields.filter(field => {
    const {
      required
    } = getSourcesByStatus(field);
    return required.length > 0;
  }).length;
  // Count unique fields that have at least one recommended source
  const recommendedCount = displayFields.filter(field => {
    const {
      recommended
    } = getSourcesByStatus(field);
    return recommended.length > 0;
  }).length;
  // Count optional fields (all optional, not just non-gift-data)
  const optionalCount = displayFields.filter(field => {
    const {
      required,
      recommended,
      optional
    } = getSourcesByStatus(field);
    return required.length === 0 && recommended.length === 0 && optional.length > 0;
  }).length;
  const handleDownloadCSV = () => {
    const fieldsToExport = showTabs ? [...otherFields, ...vmFields] : fields;
    const headers = ['Section', 'Field', 'Required For', 'Recommended For', 'Optional For'];
    const rows = fieldsToExport.map(field => {
      const {
        required,
        recommended,
        optional
      } = getSourcesByStatus(field);
      return [field.section, field.field, required.join(', '), recommended.join(', '), optional.join(', ')];
    });
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `field-requirements-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // Mobile card component for a single field
  const MobileFieldCard = ({
    field,
    isOptionalSection = false
  }: {
    field: FieldWithSources;
    isOptionalSection?: boolean;
  }) => {
    const {
      required,
      recommended,
      optional
    } = getSourcesByStatus(field);
    return <div className="bg-white border-b border-gray-200 p-4" data-id="element-55">
        <div className="mb-2" data-id="element-56">
          <span className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-57">
            {field.section}
          </span>
        </div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-58">
          {field.field}
        </h4>

        {!isOptionalSection && <>
            {required.length > 0 && <div className="mb-2" data-id="element-59">
                <span className="text-xs font-medium text-gray-600 block mb-1" data-id="element-60">
                  Required for:
                </span>
                <div className="flex flex-wrap gap-1" data-id="element-61">
                  {required.map(source => <span key={source} className="inline-flex items-center px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded" data-id="element-62">
                      {source}
                    </span>)}
                </div>
              </div>}

            {recommended.length > 0 && <div className="mb-2" data-id="element-63">
                <span className="text-xs font-medium text-gray-600 block mb-1" data-id="element-64">
                  Recommended for:
                </span>
                <div className="flex flex-wrap gap-1" data-id="element-65">
                  {recommended.map(source => <span key={source} className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded" data-id="element-66">
                      {source}
                    </span>)}
                </div>
              </div>}
          </>}

        {isOptionalSection && optional.length > 0 && <div data-id="element-67">
            <span className="text-xs font-medium text-gray-600 block mb-1" data-id="element-68">
              Optional for:
            </span>
            <div className="flex flex-wrap gap-1" data-id="element-69">
              {optional.map(source => <span key={source} className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded" data-id="element-70">
                  {source}
                </span>)}
            </div>
          </div>}
      </div>;
  };
  return <div className="space-y-4" data-id="element-71">
      {/* Tabs for VM separation (only show when mixing VM with other products) */}
      {showTabs && <div className="bg-white rounded-t-lg border border-b-0 border-gray-200" data-id="element-72">
          <div className="flex overflow-x-auto border-b border-gray-200 -mb-px scrollbar-hide" data-id="element-73">
            <button onClick={() => setActiveTab('other')} className={`flex-shrink-0 px-4 sm:px-6 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'other' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-74">
              Other Products ({otherFields.length})
            </button>
            <button onClick={() => setActiveTab('vm')} className={`flex-shrink-0 px-4 sm:px-6 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'vm' ? 'border-[#0047AB] text-[#0047AB]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-75">
              <span className="hidden sm:inline" data-id="element-76">
                Volunteer Management ({vmFields.length})
              </span>
              <span className="sm:hidden" data-id="element-77">VM ({vmFields.length})</span>
            </button>
          </div>
        </div>}

      {/* Summary */}
      <div className={`${showTabs ? 'bg-white border-x border-gray-200 px-4 sm:px-6 py-4' : 'bg-white rounded-lg border border-gray-200 px-4 py-4'}`} data-id="element-78">
        {/* Mobile: Stack layout, Desktop: Side by side */}
        <div className="flex flex-col gap-4" data-id="element-79">
          {/* Field counts - wrap on mobile */}
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm" data-id="element-80">
            <div className="flex items-center gap-1" data-id="element-81">
              <span className="font-medium text-gray-700" data-id="element-82">Total:</span>
              <span className="text-gray-900 font-semibold" data-id="element-83">
                {displayFields.length}
              </span>
            </div>
            <div className="flex items-center gap-1" data-id="element-84">
              <span className="inline-flex items-center px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded" data-id="element-85">
                Required
              </span>
              <span className="text-gray-900" data-id="element-86">{requiredCount}</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-87">
              <span className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded" data-id="element-88">
                Recommended
              </span>
              <span className="text-gray-900" data-id="element-89">{recommendedCount}</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-90">
              <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded" data-id="element-91">
                Optional
              </span>
              <span className="text-gray-900" data-id="element-92">{optionalCount}</span>
            </div>
          </div>

          {/* Download CSV Button - Full width on mobile */}
          <button onClick={handleDownloadCSV} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-[#0047AB] hover:bg-[#003580] rounded-lg transition-colors" data-id="element-93">
            <Download className="w-4 h-4" data-id="element-94" />
            Download CSV
          </button>
        </div>

        {/* Smart Ask Amounts Note */}
        {hasSmartAskAmounts && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4" data-id="element-95">
            <p className="text-xs sm:text-sm text-gray-700" data-id="element-96">
              <span className="font-semibold text-gray-900" data-id="element-97">Note:</span> Smart
              Ask Amounts requires the last 5 years of giving history in your
              gift file, as well as the most recent 6 months.
            </p>
          </div>}
      </div>

      {/* Mobile Card View - visible on small screens */}
      <div className="md:hidden" data-id="element-98">
        {/* Constituent Data Section - Mobile */}
        {constituentFields.length > 0 && <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-99">
            <button onClick={() => setShowConstituentData(!showConstituentData)} className="w-full flex items-center justify-between bg-[#0047AB]/10 border-b border-[#0047AB]/20 px-4 py-3 text-left" data-id="element-100">
              <h3 className="text-sm font-semibold text-[#0047AB]" data-id="element-101">
                Constituent Data ({constituentFields.length})
              </h3>
              {showConstituentData ? <ChevronUp className="w-5 h-5 text-[#0047AB]" data-id="element-102" /> : <ChevronDown className="w-5 h-5 text-[#0047AB]" data-id="element-103" />}
            </button>

            {showConstituentData && <div className="divide-y divide-gray-200" data-id="element-104">
                {constituentRequiredRecommended.map((field, index) => <MobileFieldCard key={`mobile-constituent-${field.field}-${index}`} field={field} data-id="element-105" />)}
                {constituentOptional.length > 0 && <>
                    <div className="bg-gray-50 px-4 py-2 border-t border-gray-200" data-id="element-106">
                      <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-107">
                        Optional Constituent Data
                      </span>
                    </div>
                    {constituentOptional.map((field, index) => <MobileFieldCard key={`mobile-constituent-optional-${field.field}-${index}`} field={field} isOptionalSection={true} data-id="element-108" />)}
                  </>}
              </div>}
          </div>}

        {/* Other Sections - Mobile */}
        {otherSectionFields.length > 0 && <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mt-4" data-id="element-109">
            {/* Required & Recommended Fields Header */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-110">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-111">
                Other Data ({otherSectionFields.length})
              </h3>
            </div>

            {/* Field Cards */}
            <div className="divide-y divide-gray-200" data-id="element-112">
              {otherRequiredRecommended.map((field, index) => <MobileFieldCard key={`mobile-other-${field.field}-${index}`} field={field} data-id="element-113" />)}
            </div>

            {/* Optional Fields */}
            {otherOptionalFields.length > 0 && <>
                <div className="bg-gray-50 px-4 py-2 border-t border-gray-200" data-id="element-114">
                  <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-115">
                    Optional
                  </span>
                </div>
                {otherOptionalFields.map((field, index) => <MobileFieldCard key={`mobile-other-optional-${field.field}-${index}`} field={field} isOptionalSection={true} data-id="element-116" />)}
              </>}
          </div>}

        {/* Gift Data Section - Mobile */}
        {giftDataFields.length > 0 && <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mt-4" data-id="element-117">
            <button onClick={() => setShowGiftData(!showGiftData)} className="w-full flex items-center justify-between bg-amber-50 border-b border-amber-200 px-4 py-3 text-left" data-id="element-118">
              <h3 className="text-sm font-semibold text-amber-900" data-id="element-119">
                Gift Data ({giftDataFields.length})
              </h3>
              {showGiftData ? <ChevronUp className="w-5 h-5 text-amber-600" data-id="element-120" /> : <ChevronDown className="w-5 h-5 text-amber-600" data-id="element-121" />}
            </button>

            {showGiftData && <div className="divide-y divide-gray-200" data-id="element-122">
                {giftDataRequiredRecommended.map((field, index) => <MobileFieldCard key={`mobile-gift-${field.field}-${index}`} field={field} data-id="element-123" />)}
                {giftDataOptional.length > 0 && <>
                    <div className="bg-gray-50 px-4 py-2 border-t border-gray-200" data-id="element-124">
                      <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-125">
                        Optional Gift Data
                      </span>
                    </div>
                    {giftDataOptional.map((field, index) => <MobileFieldCard key={`mobile-gift-optional-${field.field}-${index}`} field={field} isOptionalSection={true} data-id="element-126" />)}
                  </>}
              </div>}
          </div>}
      </div>

      {/* Desktop Table View - hidden on small screens */}
      <div className="hidden md:block" data-id="element-127">
        {/* Constituent Data Section - Desktop */}
        {constituentFields.length > 0 && <div className={`bg-white ${showTabs ? 'border-x' : 'rounded-t-lg border-t border-x'} border-gray-200 overflow-hidden`} data-id="element-128">
            {/* Constituent Data Section Header */}
            <div className="bg-[#0047AB]/10 border-b-2 border-[#0047AB]/30 px-4 py-3" data-id="element-129">
              <h3 className="text-sm font-semibold text-[#0047AB] uppercase tracking-wider" data-id="element-130">
                Constituent Data
              </h3>
            </div>

            {/* Required & Recommended Constituent Data */}
            {constituentRequiredRecommended.length > 0 && <div className="overflow-x-auto" data-id="element-131">
                <table className="w-full" data-id="element-132">
                  <thead data-id="element-133">
                    <tr className="bg-[#0047AB]/5 border-b border-gray-200" data-id="element-134">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-135">
                        Section
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-136">
                        Field
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-137">
                        Required For
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-138">
                        Recommended For
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-139">
                    {constituentRequiredRecommended.map((field, index) => {
                const {
                  required,
                  recommended
                } = getSourcesByStatus(field);
                return <tr key={`constituent-${field.field}-${index}`} className="hover:bg-[#0047AB]/5" data-id="element-140">
                          <td className="px-4 py-3 text-sm text-gray-700" data-id="element-141">
                            {field.section}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium" data-id="element-142">
                            {field.field}
                          </td>
                          <td className="px-4 py-3" data-id="element-143">
                            <div className="flex flex-wrap gap-1" data-id="element-144">
                              {required.length > 0 ? required.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded" data-id="element-145">
                                    {source}
                                  </span>) : <span className="text-xs text-gray-400" data-id="element-146">—</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-147">
                            <div className="flex flex-wrap gap-1" data-id="element-148">
                              {recommended.length > 0 ? recommended.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded" data-id="element-149">
                                    {source}
                                  </span>) : <span className="text-xs text-gray-400" data-id="element-150">—</span>}
                            </div>
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div>}

            {/* Optional Constituent Data */}
            {constituentOptional.length > 0 && <>
                <div className="bg-gray-100 border-t border-gray-300 px-4 py-2" data-id="element-151">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-152">
                    Optional Constituent Data
                  </span>
                </div>
                <div className="overflow-x-auto" data-id="element-153">
                  <table className="w-full" data-id="element-154">
                    <thead data-id="element-155">
                      <tr className="bg-gray-50 border-b border-gray-200" data-id="element-156">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-157">
                          Section
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-158">
                          Field
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" colSpan={2} data-id="element-159">
                          Optional For
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200" data-id="element-160">
                      {constituentOptional.map((field, index) => {
                  const {
                    optional
                  } = getSourcesByStatus(field);
                  return <tr key={`constituent-optional-${field.field}-${index}`} className="hover:bg-[#EFF6FF]" data-id="element-161">
                            <td className="px-4 py-3 text-sm text-gray-700" data-id="element-162">
                              {field.section}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 font-medium" data-id="element-163">
                              {field.field}
                            </td>
                            <td className="px-4 py-3" colSpan={2} data-id="element-164">
                              <div className="flex flex-wrap gap-1" data-id="element-165">
                                {optional.length > 0 ? optional.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded" data-id="element-166">
                                      {source}
                                    </span>) : <span className="text-xs text-gray-400" data-id="element-167">
                                    —
                                  </span>}
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>
              </>}
          </div>}

        {/* Other Sections - Desktop */}
        {otherSectionFields.length > 0 && <div className="bg-white border-x border-gray-200 overflow-hidden" data-id="element-168">
            {/* Other Data Section Header */}
            <div className="bg-gray-100 border-t-2 border-gray-300 px-4 py-3" data-id="element-169">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-170">
                Other Data
              </h3>
            </div>

            {/* Required & Recommended Other Data */}
            {otherRequiredRecommended.length > 0 && <div className="overflow-x-auto" data-id="element-171">
                <table className="w-full" data-id="element-172">
                  <thead data-id="element-173">
                    <tr className="bg-gray-50 border-b border-gray-200" data-id="element-174">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-175">
                        Section
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-176">
                        Field
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-177">
                        Required For
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-178">
                        Recommended For
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-179">
                    {otherRequiredRecommended.map((field, index) => {
                const {
                  required,
                  recommended
                } = getSourcesByStatus(field);
                return <tr key={`other-${field.field}-${index}`} className="hover:bg-[#EFF6FF]" data-id="element-180">
                          <td className="px-4 py-3 text-sm text-gray-700" data-id="element-181">
                            {field.section}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium" data-id="element-182">
                            {field.field}
                          </td>
                          <td className="px-4 py-3" data-id="element-183">
                            <div className="flex flex-wrap gap-1" data-id="element-184">
                              {required.length > 0 ? required.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded" data-id="element-185">
                                    {source}
                                  </span>) : <span className="text-xs text-gray-400" data-id="element-186">—</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-187">
                            <div className="flex flex-wrap gap-1" data-id="element-188">
                              {recommended.length > 0 ? recommended.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded" data-id="element-189">
                                    {source}
                                  </span>) : <span className="text-xs text-gray-400" data-id="element-190">—</span>}
                            </div>
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div>}

            {/* Optional Other Data */}
            {otherOptionalFields.length > 0 && <>
                <div className="bg-gray-100 border-t border-gray-300 px-4 py-2" data-id="element-191">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-192">
                    Optional
                  </span>
                </div>
                <div className="overflow-x-auto" data-id="element-193">
                  <table className="w-full" data-id="element-194">
                    <thead data-id="element-195">
                      <tr className="bg-gray-50 border-b border-gray-200" data-id="element-196">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-197">
                          Section
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-198">
                          Field
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" colSpan={2} data-id="element-199">
                          Optional For
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200" data-id="element-200">
                      {otherOptionalFields.map((field, index) => {
                  const {
                    optional
                  } = getSourcesByStatus(field);
                  return <tr key={`other-optional-${field.field}-${index}`} className="hover:bg-[#EFF6FF]" data-id="element-201">
                            <td className="px-4 py-3 text-sm text-gray-700" data-id="element-202">
                              {field.section}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 font-medium" data-id="element-203">
                              {field.field}
                            </td>
                            <td className="px-4 py-3" colSpan={2} data-id="element-204">
                              <div className="flex flex-wrap gap-1" data-id="element-205">
                                {optional.length > 0 ? optional.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded" data-id="element-206">
                                      {source}
                                    </span>) : <span className="text-xs text-gray-400" data-id="element-207">
                                    —
                                  </span>}
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>
              </>}
          </div>}

        {/* Gift Data Section - Desktop */}
        {giftDataFields.length > 0 && <div className={`bg-white ${constituentFields.length === 0 && otherSectionFields.length === 0 ? 'rounded-lg border' : 'rounded-b-lg border-x border-b'} border-gray-200 overflow-hidden`} data-id="element-208">
            {/* Gift Data Section Header */}
            <div className="bg-amber-50 border-t-2 border-amber-300 px-4 py-3" data-id="element-209">
              <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wider" data-id="element-210">
                Gift Data
              </h3>
            </div>

            {/* Required & Recommended Gift Data */}
            {giftDataRequiredRecommended.length > 0 && <div className="overflow-x-auto" data-id="element-211">
                <table className="w-full" data-id="element-212">
                  <thead data-id="element-213">
                    <tr className="bg-amber-50/50 border-b border-gray-200" data-id="element-214">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-215">
                        Field
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-216">
                        Required For
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-217">
                        Recommended For
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-218">
                    {giftDataRequiredRecommended.map((field, index) => {
                const {
                  required,
                  recommended
                } = getSourcesByStatus(field);
                return <tr key={`gift-${field.field}-${index}`} className="hover:bg-amber-50/30" data-id="element-219">
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium" data-id="element-220">
                            {field.field}
                          </td>
                          <td className="px-4 py-3" data-id="element-221">
                            <div className="flex flex-wrap gap-1" data-id="element-222">
                              {required.length > 0 ? required.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded" data-id="element-223">
                                    {source}
                                  </span>) : <span className="text-xs text-gray-400" data-id="element-224">—</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-225">
                            <div className="flex flex-wrap gap-1" data-id="element-226">
                              {recommended.length > 0 ? recommended.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded" data-id="element-227">
                                    {source}
                                  </span>) : <span className="text-xs text-gray-400" data-id="element-228">—</span>}
                            </div>
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div>}

            {/* Optional Gift Data */}
            {giftDataOptional.length > 0 && <>
                <div className="bg-gray-100 border-t border-gray-300 px-4 py-2" data-id="element-229">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-230">
                    Optional Gift Data
                  </span>
                </div>
                <div className="overflow-x-auto" data-id="element-231">
                  <table className="w-full" data-id="element-232">
                    <thead data-id="element-233">
                      <tr className="bg-gray-50 border-b border-gray-200" data-id="element-234">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-235">
                          Field
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" colSpan={2} data-id="element-236">
                          Optional For
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200" data-id="element-237">
                      {giftDataOptional.map((field, index) => {
                  const {
                    optional
                  } = getSourcesByStatus(field);
                  return <tr key={`gift-optional-${field.field}-${index}`} className="hover:bg-[#EFF6FF]" data-id="element-238">
                            <td className="px-4 py-3 text-sm text-gray-900 font-medium" data-id="element-239">
                              {field.field}
                            </td>
                            <td className="px-4 py-3" colSpan={2} data-id="element-240">
                              <div className="flex flex-wrap gap-1" data-id="element-241">
                                {optional.length > 0 ? optional.map(source => <span key={source} className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded" data-id="element-242">
                                      {source}
                                    </span>) : <span className="text-xs text-gray-400" data-id="element-243">
                                    —
                                  </span>}
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>
              </>}
          </div>}
      </div>
    </div>;
}